import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../services/api';
import { FaClock, FaCalendarAlt } from 'react-icons/fa';
import SEO from '../components/SEO';

function Timetable() {
  const [timetables, setTimetables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState('Class 10');
  const [selectedDay, setSelectedDay] = useState('Monday');

  useEffect(() => {
    fetchTimetable();
  }, []);

  const fetchTimetable = async () => {
    try {
      const response = await api.get('/timetable');
      console.log('📅 Fetched timetables:', response.data);
      setTimetables(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching timetable:', error);
      setTimetables([
        { id: 1, className: 'Class 10', section: 'A', dayOfWeek: 'Monday', periodNumber: 1, subject: 'Mathematics', teacherName: 'Mr. Sharma', roomNumber: 'Room 101', startTime: '08:00', endTime: '08:45' },
        { id: 2, className: 'Class 10', section: 'A', dayOfWeek: 'Monday', periodNumber: 2, subject: 'English', teacherName: 'Mrs. Verma', roomNumber: 'Room 102', startTime: '08:45', endTime: '09:30' }
      ]);
      setLoading(false);
    }
  };

  const classOptions = ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 
                        'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'];
  
  const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const filteredTimetable = timetables.filter(t => 
    t.className === selectedClass && t.dayOfWeek === selectedDay
  ).sort((a, b) => a.periodNumber - b.periodNumber);

  if (loading) {
    return (
      <>
      <SEO 
        title="Class Timetable & Schedule | Vidya Vatika World School"
        description="View class-wise timetable, exam schedules and academic calendar for all classes at Vidya Vatika World School."
        keywords="school timetable Panipat, class schedule, exam timetable, academic calendar CBSE"
        canonicalUrl="/timetable"
        ogImage="/images/og-image.jpg"
      />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
        <div className="text-center text-xl text-blue-900">Loading timetable...</div>
      </div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title="Class Timetable & Schedule | Vidya Vatika World School Panipat"
        description="View class-wise timetable, exam schedules and academic calendar for all classes at Vidya Vatika World School."
        keywords="school timetable Panipat, class schedule, exam timetable, academic calendar CBSE, daily routine Haryana"
        canonicalUrl="/timetable"
        ogImage="/images/og-image.jpg"
      />
      
      <div className="pt-20 min-h-screen bg-gray-50">
        {/* Header Banner */}
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <motion.h1 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-5xl font-bold font-display mb-4"
            >
              Class Timetable
            </motion.h1>
            <p className="text-xl text-blue-100">Organized schedule for academic excellence</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12">
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <div className="flex flex-wrap gap-4 justify-center">
              <div className="flex items-center gap-3">
                <FaCalendarAlt className="text-blue-900 text-xl" />
                <label className="font-semibold text-gray-700">Class:</label>
                <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}
                  className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-900">
                  {classOptions.map(cls => <option key={cls} value={cls}>{cls}</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3">
                <FaClock className="text-blue-900 text-xl" />
                <label className="font-semibold text-gray-700">Day:</label>
                <select value={selectedDay} onChange={(e) => setSelectedDay(e.target.value)}
                  className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-900">
                  {dayOptions.map(day => <option key={day} value={day}>{day}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Timetable Table */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Period</th>
                    <th className="px-6 py-4 text-left font-semibold">Subject</th>
                    <th className="px-6 py-4 text-left font-semibold">Teacher</th>
                    <th className="px-6 py-4 text-left font-semibold">Room</th>
                    <th className="px-6 py-4 text-left font-semibold">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredTimetable.map((item, index) => (
                    <tr key={item.id} className={`${index % 2 === 0 ? 'bg-white' : 'bg-blue-50'} hover:bg-blue-100 transition`}>
                      <td className="px-6 py-4">
                        <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                          Period {item.periodNumber}
                        </span>
                      </td>
                      <td className="px-6 py-4 font-semibold text-gray-800">{item.subject}</td>
                      <td className="px-6 py-4 text-gray-700">{item.teacherName}</td>
                      <td className="px-6 py-4 text-gray-600">{item.roomNumber}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <FaClock className="text-blue-900" />
                          <span>{item.startTime} - {item.endTime}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {filteredTimetable.length === 0 && (
            <div className="text-center text-gray-600 mt-12 py-8">
              <p className="text-xl">No timetable available for {selectedClass} on {selectedDay}</p>
              <p className="text-sm mt-2">Please select a different class or day</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Timetable;
